/**
 * chatbotSampleQuestions.js
 * Single source of truth for the DRIMS flood chatbot sample questions.
 *
 * Exports:
 *   SAMPLE_QUESTION_CATEGORIES — full curated list (50+ questions).
 *   getRandomQuestions(n)       — draws n distinct questions from scope-agnostic
 *                                 categories only (excludes categories flagged
 *                                 excludeFromRandom, e.g. place comparisons that
 *                                 need district/state access).
 *
 * Every question here is confirmed to be handled by the chatbot backend
 * (single metrics, breakdowns, date ranges, year/metric comparisons, multi-metric,
 * ranking, place comparison). Keep this list in sync with backend capabilities.
 */

/**
 * All curated sample question categories.
 * A category with `excludeFromRandom: true` is shown on the help page but never
 * surfaced as a random suggestion chip (place comparisons depend on the user's
 * access level).
 *
 * @type {Array<{ category: string, questions: string[], excludeFromRandom?: boolean }>}
 */
export const SAMPLE_QUESTION_CATEGORIES = [
  {
    category: "Metrics & breakdowns",
    questions: [
      "How many deaths in 2024?",
      "Missing persons in 2024",
      "People in relief camps in 2024",
      "Camp inmates for 2025",
      "Total camp inmates for 2025",
      "Non camp inmates for 2025",
      "Houses damaged in 2024",
      "Affected population in 2024",
      "Crop area affected in 2024",
      "Livestock lost in 2024",
      "Infrastructure damage in 2024",
      "Rescue operations in 2024",
      "Affected villages in 2024",
      "Number of relief camps in 2024",
      "Number of relief distribution centres in 2024",
      "Relief items distributed in 2024",
      "How many reports submitted in 2024",
      "Report status breakdown for 2024",
    ],
  },
  {
    category: "Date ranges & relative dates",
    questions: [
      "Deaths in Q1 2024",
      "Camp inmates in Q3 2024",
      "Relief camps in the last 3 months",
      "Affected population in the last 6 months",
      "Camp inmates since January 2024",
      "Deaths since 2020",
      "Deaths in monsoon 2024",
      "Camp inmates for last year",
      "Affected population this year",
      "Deaths from January to March 2024",
      "Houses damaged between 2020 and 2024",
    ],
  },
  {
    category: "Compare across years",
    questions: [
      "Compare deaths for 2024 and 2025",
      "Compare camp inmates in 2023 and 2024",
      "Compare houses damaged for 2024 and 2025",
      "Compare crop area for 2023 and 2024",
      "Compare affected population in 2023, 2024 and 2025",
      "Add deaths for 2024 and 2025",
      "Deaths in 2024 minus 2023",
    ],
  },
  {
    category: "Compare two metrics",
    questions: [
      "Compare deaths and missing persons in 2024",
      "Compare people in camps and relief distribution centres for 2024",
      "Compare relief camps and relief distribution centres in 2024",
      "Compare camp inmates and non camp inmates for 2025",
      "Compare houses damaged and livestock lost in 2024",
      "Difference between affected population and affected villages in 2024",
    ],
  },
  {
    category: "Multiple metrics at once",
    questions: [
      "Deaths, affected population and crop area for 2024",
      "Camp inmates and non camp inmates for 2025",
      "Deaths, missing persons and affected population in 2024",
      "Relief camps, camp inmates and relief distribution for 2024",
    ],
  },
  {
    category: "Ranking & top lists",
    questions: [
      "Which district had the most deaths in 2024?",
      "Top 3 districts by affected population in 2025",
      "Which district had the fewest relief camps in 2024?",
      "Top 5 districts by crop area affected in 2024",
      "Which district had the most camp inmates in 2024?",
      "Rank districts by missing persons in 2024",
    ],
  },
  {
    // Place comparisons depend on the user's access level (district/state),
    // so they are shown on the page but excluded from random suggestion chips.
    category: "Compare places",
    excludeFromRandom: true,
    questions: [
      "Deaths in Kamrup vs Barpeta in 2024",
      "Compare relief camps between Sonitpur and Nagaon in 2024",
      "Camp inmates in Dhubri vs Cachar for 2024",
    ],
  },
];

/**
 * Build the flat pool of scope-agnostic questions (excludes categories flagged
 * excludeFromRandom). Computed once at module load; getRandomQuestions() reads it.
 *
 * @type {string[]}
 */
const SCOPE_AGNOSTIC_POOL = SAMPLE_QUESTION_CATEGORIES.filter(
  (c) => !c.excludeFromRandom
).flatMap(({ questions }) => questions);

/**
 * Returns n distinct, randomly chosen questions drawn only from scope-agnostic
 * categories.  If n exceeds the pool size the full pool is returned (shuffled,
 * no duplicates, no crash).
 *
 * Pure function — no side effects, no external dependencies; safe to unit-test.
 *
 * @param {number} n  How many questions to return.
 * @returns {string[]}
 */
export function getRandomQuestions(n) {
  // Defensive: treat non-positive n as 0
  const count = Math.max(0, Math.floor(n));
  if (count === 0 || SCOPE_AGNOSTIC_POOL.length === 0) return [];

  // Shuffle a copy with Fisher-Yates then take the first `count` items.
  // Using Math.random() is fine here — this is display-only randomness.
  const shuffled = [...SCOPE_AGNOSTIC_POOL];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    // swap
    const tmp = shuffled[i];
    shuffled[i] = shuffled[j];
    shuffled[j] = tmp;
  }

  // Clamp to pool size so we never return duplicates or exceed what exists
  return shuffled.slice(0, Math.min(count, shuffled.length));
}
